import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Home,
  Users,
  UserCheck,
  Trophy,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Shield,
  Calendar,
  Settings,
  Menu,
  X,
  Wallet,
  Megaphone,
  FileText,
  Flag,
  Image,
  Handshake,
  Package,
  ShoppingBag,
  Shapes,
  Check,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { logout } from "../../store/slices/userSlice";
import { useState, useEffect, useRef } from "react";
import type { LucideIcon } from "lucide-react";
import {
  canAccessHome,
  getDefaultRoute,
  canAccessTournaments,
  canAccessPlayers,
  canAccessRepresenters,
  canAccessUserManagement,
  canAccessCourtReservations,
  canAccessConfiguration,
  canAccessTransactions,
  canAccessReports,
  canManageAnnouncements,
  canManageEvents,
  canManagePartners,
  canAccessGalleries,
  canManageShop,
} from "../../utils/authorization";
import { BrandLogo } from "../../ui";
import "./styles.scss";

type NavItem = { to: string; label: string; icon: LucideIcon; can: (user: any) => boolean; end?: boolean };
type NavSection = { title?: string; items: NavItem[] };

// One list for the desktop rail and the phone menu. A section only shows when the user can see one of its items.
const NAV_SECTIONS: NavSection[] = [
  { items: [{ to: "/dashboard", label: "Home", icon: Home, can: canAccessHome, end: true }] },
  {
    title: "Competitions",
    items: [
      { to: "/dashboard/tournaments", label: "Tournaments", icon: Trophy, can: canAccessTournaments },
      { to: "/dashboard/events", label: "Events", icon: Flag, can: canManageEvents },
      { to: "/dashboard/galleries", label: "Galleries", icon: Image, can: canAccessGalleries },
    ],
  },
  {
    title: "People",
    items: [
      { to: "/dashboard/players", label: "Players", icon: Users, can: canAccessPlayers },
      { to: "/dashboard/representers", label: "Representers", icon: UserCheck, can: canAccessRepresenters },
    ],
  },
  {
    title: "Bookings & Finance",
    items: [
      { to: "/dashboard/court-reservations", label: "Court Reservations", icon: Calendar, can: canAccessCourtReservations },
      { to: "/dashboard/transactions", label: "Transactions", icon: Wallet, can: canAccessTransactions },
      { to: "/dashboard/reports", label: "Reports", icon: FileText, can: canAccessReports },
    ],
  },
  {
    title: "Shop",
    items: [
      { to: "/dashboard/shop/catalog", label: "Catalog setup", icon: Shapes, can: canManageShop },
      { to: "/dashboard/shop/products", label: "Products", icon: Package, can: canManageShop },
      { to: "/dashboard/shop/orders", label: "Orders", icon: ShoppingBag, can: canManageShop },
    ],
  },
  {
    title: "Website",
    items: [
      { to: "/dashboard/announcements", label: "Announcements", icon: Megaphone, can: canManageAnnouncements },
      { to: "/dashboard/partners", label: "Partners", icon: Handshake, can: canManagePartners },
    ],
  },
  {
    title: "Admin",
    items: [
      { to: "/dashboard/configuration", label: "Configuration", icon: Settings, can: canAccessConfiguration },
      { to: "/dashboard/user-management", label: "User Management", icon: Shield, can: canAccessUserManagement },
    ],
  },
];

const Sidebar = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector((state) => state.auth.currentUser);

  const isAddTournament = window.location.pathname.split("/").includes("add-tournament");
  const isAddCategory = window.location.pathname.split("/").includes("add-category");
  const isCreateSchedule = window.location.pathname.split("/").includes("create-schedule");
  const isAdd = isAddCategory || isAddTournament;

  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<{ label: string; completed: boolean; current: boolean }[]>([]);
  const isMobileMenuOpenRef = useRef(isMobileMenuOpen);

  // Keep ref in sync with state
  useEffect(() => {
    isMobileMenuOpenRef.current = isMobileMenuOpen;
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      const wasMobile = isMobile;
      const nowMobile = window.innerWidth < 768;
      setIsMobile(nowMobile);
      if (nowMobile) {
        setIsCollapsed(false);
      } else {
        // Close mobile menu when switching to desktop
        if (wasMobile && isMobileMenuOpenRef.current) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  useEffect(() => {
    if (isAdd) {
      setIsCollapsed(false);

      setSteps(
        isAddTournament
          ? [
              { label: "Name & Description", completed: false, current: true },
              { label: "Type", completed: false, current: false },
              { label: "Date & Location", completed: false, current: false },
              { label: "Final Touches", completed: false, current: false },
            ]
          : isAddCategory
            ? [
                { label: "Category Details", completed: false, current: true },
                { label: "Format", completed: false, current: false },
                { label: "Teams & Players", completed: false, current: false },
                { label: "Prizes & Awards", completed: false, current: false },
                { label: "Fees", completed: false, current: false },
              ]
            : [],
      );

      const step = searchParams.get("step");
      const reset = searchParams.get("reset");

      if (step) {
        const currentStep = parseInt(step) - 1;

        setCurrentStep(currentStep);
        setSteps((prev) =>
          prev.map((item, id) => {
            if (id < currentStep) return { ...item, completed: true };
            if (id === currentStep) return { ...item, current: true };
            return item;
          }),
        );
      } else {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("step", "1");
        setSearchParams(newParams);
      }

      if (reset) {
        setSteps((prev) =>
          prev.map((item, id) => {
            if (id === 0) return { ...item, current: true, completed: false };
            else return { ...item, current: false, completed: false };
          }),
        );

        const newParams = new URLSearchParams(searchParams);
        newParams.delete("reset");
        setSearchParams(newParams);
      }
    }
  }, [searchParams, isAdd]);

  const handleLogout = () => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
    dispatch(logout());
    navigate("/");
  };

  const handleCancel = () => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
    const goTo = searchParams.get("goTo");

    if (isAddTournament) {
      if (goTo) navigate(`/dashboard`);
      else navigate("/dashboard/tournaments");
    }

    if (isAddCategory) {
      const tournamentId = window.location.pathname.split("/")[3];
      if (goTo) navigate(`/dashboard/tournaments/${tournamentId}/categories`);
      else navigate(`/dashboard/tournaments/${tournamentId}`);
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.can(currentUser)),
  })).filter((section) => section.items.length > 0);

  const renderSections = (iconSize: number, onNavigate?: () => void) =>
    visibleSections.map((section, i) => (
      <div className="nav-section" key={section.title ?? `section-${i}`}>
        {section.title && <p className="nav-section-label">{section.title}</p>}
        {section.items.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} onClick={onNavigate} data-tooltip={isCollapsed || isCreateSchedule ? label : undefined}>
            <Icon size={iconSize} aria-hidden="true" /> <span>{label}</span>
          </NavLink>
        ))}
      </div>
    ));

  const LogoutIcon = <LogOut size={16} aria-hidden="true" />;

  // The logo goes to Home ("/" would bounce back to the last visited page); roles without Home go to their landing page
  const goHome = () => {
    setIsMobileMenuOpen(false);
    navigate(canAccessHome(currentUser) ? "/dashboard" : getDefaultRoute(currentUser));
  };

  return (
    <>
      <aside className={`sidebar ${isCollapsed || isMobile || isCreateSchedule ? "collapsed" : ""}`}>
        <div className="sidebar-main">
          <div className="sidebar-header">
            <button type="button" className="sidebar-brand" onClick={goHome} aria-label="The Clash — Home">
              <BrandLogo className="sidebar-logo" />
              {!isCollapsed && !isMobile && <span className="subtitle">Admin Dashboard</span>}
            </button>
            {isMobile && !isAdd && (
              <button
                type="button"
                className="mobile-menu-toggle"
                onClick={toggleMobileMenu}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            )}
            {!isAdd && !isCreateSchedule && !isMobile && (
              <button
                type="button"
                className="collapse-toggle"
                onClick={toggleCollapse}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                data-tooltip={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {isCollapsed ? (
                  <ChevronRight size={16} className="toggle-icon" />
                ) : (
                  <ChevronLeft size={16} className="toggle-icon" />
                )}
              </button>
            )}
          </div>
          {!isMobile && (
            <nav className="sidebar-nav">
              {isAdd ? (
                <>
                  <div className="details">
                    {isAddCategory && <p className="tournament-name">{searchParams.get("name")}</p>}
                    <p className="create-new">
                      Create new {isAddTournament ? "tournament" : isAddCategory ? "category" : ""}
                    </p>
                  </div>
                  <ol className="steps-list">
                    {steps.map((item, id) => {
                      const isDone = item.completed && id !== currentStep;
                      const isCurrent = id === currentStep;
                      return (
                        <li
                          key={id}
                          className={`step-container ${item.current || item.completed ? "active" : ""} ${
                            !item.completed && !item.current ? "disabled" : ""
                          } ${isCurrent ? "is-current" : ""}`}
                          aria-current={isCurrent ? "step" : undefined}
                          onClick={
                            item.completed || item.current
                              ? () => {
                                  const newParams = new URLSearchParams(searchParams);
                                  newParams.set("step", `${id + 1}`);
                                  setSearchParams(newParams);
                                  setCurrentStep(id);
                                }
                              : undefined
                          }
                        >
                          <span className={`circle ${isDone ? "completed" : ""}`}>
                            {isDone ? <Check size={14} strokeWidth={3} aria-hidden="true" /> : id + 1}
                          </span>
                          <span className={`label ${isDone ? "underline" : ""}`}>{item.label}</span>
                        </li>
                      );
                    })}
                  </ol>
                </>
              ) : (
                renderSections(18)
              )}
            </nav>
          )}
        </div>
        {!isMobile && (
          <div className="sidebar-footer">
            <button
              type="button"
              className="logout-btn"
              onClick={isAdd ? handleCancel : handleLogout}
              data-tooltip={isCollapsed || isCreateSchedule ? (isAdd ? "Cancel & Exit" : "Logout") : undefined}
            >
              {LogoutIcon} <span>{isAdd ? "Cancel & Exit" : "Logout"}</span>
            </button>
          </div>
        )}
      </aside>

      {/* Mobile Menu Overlay */}
      {isMobile && isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={toggleMobileMenu}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <button type="button" className="mobile-menu-brand" onClick={goHome} aria-label="The Clash — Home">
                <BrandLogo className="mobile-menu-logo" />
              </button>
              <button type="button" className="mobile-menu-close" onClick={toggleMobileMenu} aria-label="Close menu">
                <X size={22} />
              </button>
            </div>
            <nav className="mobile-menu-nav">{renderSections(18, handleMobileNavClick)}</nav>
            <div className="mobile-menu-footer">
              <button type="button" className="mobile-logout-btn" onClick={isAdd ? handleCancel : handleLogout}>
                {LogoutIcon} <span>{isAdd ? "Cancel & Exit" : "Logout"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
